import { Navigate } from 'react-router-dom';
import { Header } from '@/components/landing/Header';
import { Footer } from '@/components/landing/Footer';
import { Features } from '@/components/landing/Features';
import { SectionGlow } from '@/components/landing/SectionGlow';
import { AppStoreBadge } from '@/components/AppStoreBadges';
import { useAuth } from '@/hooks/useAuth';
import { SEOHead } from '@/components/SEOHead';
import { Helmet } from 'react-helmet-async';

export default function Index() {
  const { user, loading } = useAuth();

  if (!loading && user) {
    return <Navigate to="/library" replace />;
  }

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="PodBrief | AI Podcast & YouTube Summarizer"
        description="Turn any podcast or YouTube video into a short summary you can read or listen to in minutes. 10 one-hour shows a month means 9 hours saved."
        url="https://podbrief.io/"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'SoftwareApplication',
            name: 'PodBrief',
            applicationCategory: 'MultimediaApplication',
            operatingSystem: 'Web, iOS',
            url: 'https://podbrief.io',
            description:
              'PodBrief turns podcasts and YouTube videos into short AI-narrated summaries you can read or listen to in minutes.',
            offers: {
              '@type': 'Offer',
              price: '0',
              priceCurrency: 'USD',
              description: 'Free podcast listening with 5 AI summaries included',
            },
          })}
        </script>
      </Helmet>

      <Header />

      <main className="pt-16 relative z-10">
        {/* Hero */}
        <section className="relative px-6 pt-24 pb-24 md:pt-32 md:pb-32 lg:pt-40 lg:pb-40 text-center overflow-hidden">
          <SectionGlow position="top" />

          <div className="relative max-w-5xl mx-auto">
            <h1
              className="font-display font-normal text-text mb-8 animate-fade-in-up"
              style={{
                fontSize: 'clamp(3rem, 11vw, 6.5rem)',
                lineHeight: 0.92,
                letterSpacing: '-0.022em',
              }}
            >
              AI Podcast &amp; YouTube{' '}
              <em
                className="not-italic"
                style={{ fontStyle: 'italic', color: '#e0b352' }}
              >
                Summarizer
              </em>
            </h1>

            <p
              className="font-body mx-auto max-w-3xl mb-6 text-text/[0.62] animate-fade-in-up-delay-1"
              style={{
                fontSize: 'clamp(1.0625rem, 1.5vw, 1.25rem)',
                lineHeight: 1.5,
                letterSpacing: '-0.005em',
              }}
            >
              PodBrief turns podcasts and YouTube videos into short summaries
              you can get through in minutes.{' '}
              <span className="text-text font-bold">
                Save hours every month
              </span>{' '}
              without missing what matters.
            </p>

            <div className="flex flex-col items-center gap-4 animate-fade-in-up-delay-2">
              <AppStoreBadge size="h-14" />
            </div>
          </div>
        </section>

        <Features />
      </main>

      <Footer />
    </div>
  );
}
